"use client";

import { Fragment, useEffect, useRef, useState } from "react";
import LazyOnVisible from "./LazyOnVisible";
import styles from "./ScrollingFeaturesSection.module.css";

// Resume-playback illustration plays the original VSL clip behind the overlay,
// looping just the first few seconds.
const RESUME_VIDEO_SRC = "https://videos.flare.link/1767968352766-6v874q8/screen/full.mp4";
const RESUME_POSTER_SRC = "https://videos.flare.link/1767968352766-6v874q8/thumbnail.jpg";
const RESUME_LOOP_SECONDS = 5;

const DEMO_LOADERS = {
  vsl: () => import("./VslDemo"),
  progressive: () => import("./ProgressiveDemo"),
  subtitles: () => import("./SubtitlesDemo"),
  chapters: () => import("./ChaptersDemo"),
};

/* Inline stroke icons (lucide-style) — one per feature. Sized via
   currentColor + 1.6 stroke to match the rest of the landing UI. */
const ICONS = {
  vsl: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m10 9 5 3-5 3z" fill="currentColor" stroke="none" />
    </svg>
  ),
  progressive: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {/* fast stopwatch — clock body with motion lines */}
      <circle cx="14.5" cy="13" r="6.5" />
      <path d="M14.5 13V9" />
      <path d="m14.5 13 3 2" />
      <path d="M12.5 4.5h4" />
      <path d="M14.5 4.5v2" />
      <path d="m19.8 8 1.4-1.4" />
      <path d="M3 9.5h5.5" />
      <path d="M2 13h4.5" />
      <path d="M4 16.5h4.5" />
    </svg>
  ),
  subtitles: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M7 14h4M13 14h4M7 10.5h6" />
    </svg>
  ),
  chapters: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 6h13M8 12h13M8 18h13" />
      <path d="M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  ),
  password: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  resume: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
      <path d="m10 9 5 3-5 3z" fill="currentColor" stroke="none" />
    </svg>
  ),
};

const FEATURES = [
  {
    id: "vsl",
    title: "VSL mode",
    description:
      "A player theme packed with engagement-boosting features that keep viewers watching",
  },
  {
    id: "progressive",
    title: "Progressive timeline",
    description:
      "Make the progress bar faster initially to make the video seem shorter and increase watch time.",
  },
  {
    id: "subtitles",
    title: "Multi-language subtitles",
    description:
      "Auto-generated, editable, and selectable per viewer. Captions in every market you sell to.",
  },
  {
    id: "chapters",
    title: "Chapters",
    description:
      "Jump-to-section navigation built into the player so viewers find the part they care about.",
  },
  {
    id: "resume",
    title: "Resume playback",
    description:
      "Returning viewers pick up exactly where they left off or restart from the top.",
  },
  {
    id: "password",
    title: "Password protection",
    description:
      "Gate sensitive videos with a password. Share only with the people who should see it.",
  },
];

function PasswordIllustration() {
  return (
    <div className={styles.illuFrame}>
      <div className={styles.illuVideoBlurred} aria-hidden="true" />
      <div className={styles.illuPasswordPanel}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        <span className={styles.illuPasswordTitle}>Enter password</span>
        <div className={styles.illuPasswordInput}>
          <span className={styles.illuPasswordDots}>••••••••</span>
        </div>
        <div className={styles.illuPasswordButton}>Watch video</div>
      </div>
    </div>
  );
}

function ResumePlayIllustration() {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    const v = videoRef.current;
    if (!el || !v) return undefined;

    // Loop just the first RESUME_LOOP_SECONDS behind the overlay.
    const onTime = () => {
      if (v.currentTime >= RESUME_LOOP_SECONDS) {
        try { v.currentTime = 0; } catch (_) {}
      }
    };
    v.addEventListener("timeupdate", onTime);

    // Lazy-load + play only while the section is on screen (matches the
    // other demos), so we don't fetch the clip for an off-screen tab.
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!loadedRef.current) {
            v.src = RESUME_VIDEO_SRC;
            loadedRef.current = true;
          }
          v.play().catch(() => {});
        } else if (loadedRef.current) {
          v.pause();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      v.removeEventListener("timeupdate", onTime);
    };
  }, []);

  return (
    <div className={styles.illuFrame} ref={containerRef}>
      <video
        ref={videoRef}
        className={styles.illuResumeVideo}
        poster={RESUME_POSTER_SRC}
        muted
        playsInline
        preload="none"
        aria-hidden="true"
      />
      <div className={styles.illuResumeBackdrop} aria-hidden="true" />
      <div className={styles.illuResumeCard}>
        <p className={styles.illuResumeBody}>
          You&apos;ve started this video before. Do you want to resume from where you left off, or restart?
        </p>
        <div className={styles.illuResumeActions}>
          <button type="button" className={styles.illuResumeBtnPrimary} tabIndex={-1}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5.14v13.72c0 .79.87 1.27 1.54.85l10.84-6.86a1 1 0 0 0 0-1.7L9.54 4.29A1 1 0 0 0 8 5.14z" />
            </svg>
            Resume from <span className={styles.illuResumeTime}>1:42</span>
          </button>
          <button type="button" className={styles.illuResumeBtnSecondary} tabIndex={-1}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
              <path d="M3 3v5h5" />
            </svg>
            Start over
          </button>
        </div>
      </div>
    </div>
  );
}

function DynamicDemoWrapper({ children }) {
  return (
    <div className={styles.illuFrame}>
      {children}
    </div>
  );
}

const ILLUSTRATIONS = {
  vsl: () => (
    <DynamicDemoWrapper>
      <LazyOnVisible loader={DEMO_LOADERS.vsl} />
    </DynamicDemoWrapper>
  ),
  progressive: () => (
    <DynamicDemoWrapper>
      <LazyOnVisible loader={DEMO_LOADERS.progressive} />
    </DynamicDemoWrapper>
  ),
  subtitles: () => (
    <DynamicDemoWrapper>
      <LazyOnVisible loader={DEMO_LOADERS.subtitles} />
    </DynamicDemoWrapper>
  ),
  chapters: () => (
    <DynamicDemoWrapper>
      <LazyOnVisible loader={DEMO_LOADERS.chapters} />
    </DynamicDemoWrapper>
  ),
  password: PasswordIllustration,
  resume: ResumePlayIllustration,
};

const DEMO_IDS = new Set(["vsl", "progressive", "subtitles", "chapters"]);
// Layers whose illustration is a full-bleed video/background (no inset padding,
// so it fills the frame edge-to-edge instead of sitting in a black margin).
const FULL_BLEED_IDS = new Set([...DEMO_IDS, "resume", "password"]);

export default function ScrollingFeaturesSection() {
  const [active, setActive] = useState(FEATURES[0].id);

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          <p className={styles.subtitleLarge}>Increase click-through rate and watch time</p>
          <h2 className={styles.title}>Improve the videos <br className={styles.mobileBreak} /> that drive your business</h2>
          
        </div>

        {/* Wrapper lets the elevation bloom extend beyond the panel edges
            without being clipped by the panel's own overflow. */}
        <div className={styles.illustrationWrap}>
          {/* One panel holding both the feature boxes (left) and the live
              preview (right). */}
          <div className={styles.illustrationCol}>
            <ul className={styles.featuresGrid} role="tablist" aria-label="Player features">
              {FEATURES.map((f) => {
                const isActive = f.id === active;
                return (
                  <Fragment key={f.id}>
                    <li className={styles.featureItem}>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={`${styles.featureButton} ${isActive ? styles.featureButtonActive : ""}`}
                        onClick={() => setActive(f.id)}
                      >
                        <span className={styles.featureIcon} aria-hidden="true">
                          {ICONS[f.id]}
                        </span>
                        <span className={styles.featureTitle}>{f.title}</span>
                      </button>
                    </li>
                    {/* Info box opens right beneath the active item. Its height
                        is fixed and only one is ever shown, so the boxes keep
                        their height and the column's total height is constant —
                        the items below just shift down. */}
                    {isActive && (
                      <li className={styles.featureInfo} aria-hidden="true">
                        {f.description}
                      </li>
                    )}
                  </Fragment>
                );
              })}
            </ul>

            <div className={styles.illustrationSticky}>
              {Object.entries(ILLUSTRATIONS).map(([id, Component]) => (
                <div
                  key={id}
                  className={`${styles.illuLayer} ${FULL_BLEED_IDS.has(id) ? styles.illuLayerDemo : ""} ${active === id ? styles.illuLayerActive : ""}`}
                  aria-hidden={active !== id}
                >
                  <Component />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
